import { Injectable, inject } from '@angular/core';
import { GeneralActions } from '@hunting-nature/Secacam/shared/redux/general/general.actions';
import { DOWNLOAD_FILE_ERROR } from '@hunting-nature/shared-core/enums/download-file-error.enum';
import { SHARE_ERROR } from '@hunting-nature/shared-core/enums/share-error.enum';
import type { DeleteImageError } from '@hunting-nature/shared-core/errors/delete-image.error';
import { ToastController } from '@ionic/angular';
import { TranslocoService } from '@jsverse/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, from, map, of, switchMap, tap, withLatestFrom } from 'rxjs';

import { GeneralSelectors } from '../../../../shared/redux/general/general.reducer';
import { ShopService } from '../../../../shared/services/shop.service';
import { CameraSelectors } from '../../../cameras/redux/cameras.reducer';
import { FilterSelectors } from '../filter/redux/filter.reducer';
import { GalleryService } from '../services/gallery.service';
import { ImageService } from '../services/image.service';
import { VideoRequestService } from '../services/video-request.service';
import { VideoRequestOutcomeValues, VideoRequestErrorValues } from '../video-request.types';

import { ImageActions } from './image.actions';
import { ImageSelectors } from './image.reducer';

@Injectable()
export class ImageEffects {
  // services
  private readonly actions$ = inject(Actions);
  private readonly galleryService = inject(GalleryService);
  private readonly imageService = inject(ImageService);
  private readonly toastController = inject(ToastController);
  private readonly store = inject(Store);
  private readonly videoRequestService = inject(VideoRequestService);
  private readonly shopService = inject(ShopService);
  private readonly transloco = inject(TranslocoService);

  // Gallery-wide toggles (no image required)
  toggleAiDetectionFramesGalleryWide$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ImageActions.toggleAiDetectionFramesGalleryWide),
      switchMap(() => of(ImageActions.toggleAiDetectionFramesSucceeded()))
    )
  );

  toggleColorizedVersionGalleryWide$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ImageActions.toggleColorizedVersionGalleryWide),
      switchMap(() => of(ImageActions.toggleColorizedVersionSucceeded()))
    )
  );

  downloadImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ImageActions.downloadImage),
      switchMap(action =>
        this.galleryService.saveImageToAlbum$(action.image, action.downloadVideo, 'Secacam', 'secacam').pipe(
          map(_result => ImageActions.downloadImageSucceeded()),
          catchError((error: unknown) => {
            console.error(error);
            return of(
              ImageActions.downloadImageFailed({
                error: DOWNLOAD_FILE_ERROR.GENERIC,
              })
            );
          })
        )
      )
    )
  );

  // save into album // TODO: make album name variable!
  shareImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ImageActions.shareImage),
      switchMap(action =>
        this.galleryService.shareImage$(action.image, action.shareVideos).pipe(
          map(() => ImageActions.shareImageSucceeded()),
          catchError((error: unknown) => {
            console.error(error);
            return of(
              ImageActions.shareImageFailed({
                error: SHARE_ERROR.GENERIC,
              })
            );
          })
        )
      )
    )
  );

  setImageAsFavorite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ImageActions.setImageAsFavorite),
      map(() => GeneralActions.openComingSoonModal())
    )
  );

  deleteImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ImageActions.deleteImage),
      withLatestFrom(this.store.select(FilterSelectors.isParametersApplied)),
      switchMap(([{ imageId }, filterActive]) =>
        this.galleryService.deleteImage$(imageId).pipe(
          map(() =>
            ImageActions.deleteImageSucceeded({
              imageId,
              filterActive,
            })
          ),
          catchError((error: unknown) => {
            console.error(error);
            return of(
              ImageActions.deleteImageFailed({
                error: error as DeleteImageError,
                imageId,
              })
            );
          })
        )
      )
    )
  );

  showPositionOfImageInMap$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ImageActions.showPositionOfImageInMap),
      map(() => GeneralActions.openComingSoonModal())
    )
  );

  toggleAiDetectionFramesOrIconClicked$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ImageActions.toggleAiDetectionFrames, ImageActions.toggleAiDetectionFramesIconClicked),
      switchMap(() => {
        const hasAiFramesSubscription = true;
        if (hasAiFramesSubscription) {
          return of(ImageActions.toggleAiDetectionFramesSucceeded());
        }

        return of(GeneralActions.openComingSoonModal());
      })
    )
  );

  toggleColorizedVersionOrIconClicked$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ImageActions.toggleColorizedVersion, ImageActions.toggleColorizedVersionIconClicked),
      switchMap(({ image, colorizedVersionVisible }) => {
        if (this.imageService.shouldShowColorizedRequirementsDialog(image, colorizedVersionVisible)) {
          return of(ImageActions.openAiColoredNightRequirementsDialog());
        }
        return of(ImageActions.toggleColorizedVersionSucceeded());
      })
    )
  );

  requestVideo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ImageActions.requestVideo),
      withLatestFrom(
        this.store.select(CameraSelectors.getCameraDetails),
        this.store.select(CameraSelectors.getAllCameras),
        this.store.select(GeneralSelectors.isOfflineMode)
      ),
      switchMap(([{ imageId, imei }, cameraDetails, allCameras, isOffline]) => {
        const camera = cameraDetails?.imei === imei ? cameraDetails : allCameras.find(c => c.imei === imei);
        return this.videoRequestService.evaluateVideoRequest$(camera, imei, isOffline).pipe(
          switchMap(({ outcome, modal, noRequestsLeftVariant, cameraIdentCode }) => {
            if (outcome === VideoRequestOutcomeValues.REQUEST && cameraIdentCode) {
              return this.videoRequestService.requestVideo$(cameraIdentCode, imageId).pipe(
                map(() => ImageActions.requestVideoSucceeded({ imageId })),
                catchError(() => of(ImageActions.requestVideoFailed({ imageId, error: VideoRequestErrorValues.REQUEST_FAILED })))
              );
            }
            if (outcome === VideoRequestOutcomeValues.SHOW_MODAL_OPERATOR || outcome === VideoRequestOutcomeValues.SHOW_MODAL_NO_REQUESTS) {
              return of(ImageActions.showVideoModal({ modal: modal!, noRequestsLeftVariant, imageId, cameraIdentCode }));
            }
            if (outcome === VideoRequestOutcomeValues.BLOCKED_NO_PERMISSION) {
              return of(ImageActions.requestVideoFailed({ imageId, error: VideoRequestErrorValues.NO_PERMISSION }));
            }
            return of(ImageActions.cancelVideoRequest());
          }),
          catchError(() => of(ImageActions.cancelVideoRequest()))
        );
      })
    )
  );

  confirmVideoRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ImageActions.confirmVideoRequest),
      switchMap(({ imageId, cameraIdentCode }) =>
        this.videoRequestService.requestVideo$(cameraIdentCode, imageId).pipe(
          map(() => ImageActions.confirmVideoRequestSucceeded({ imageId })),
          catchError((_error: unknown) => of(ImageActions.confirmVideoRequestFailed({ imageId, error: VideoRequestErrorValues.REQUEST_FAILED })))
        )
      )
    )
  );

  retryVideoRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ImageActions.retryVideoRequest),
      switchMap(({ imageId, imei }) =>
        this.videoRequestService.findCameraAndRequestVideo$(imei, imageId).pipe(
          map(() => ImageActions.requestVideoSucceeded({ imageId })),
          catchError(() => of(ImageActions.requestVideoFailed({ imageId, error: VideoRequestErrorValues.REQUEST_FAILED })))
        )
      )
    )
  );

  confirmVideoRequestFromModal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ImageActions.confirmVideoRequestFromModal),
      withLatestFrom(this.store.select(ImageSelectors.pendingVideoImageId), this.store.select(ImageSelectors.pendingVideoCameraIdentCode)),
      switchMap(([, imageId, cameraIdentCode]) => {
        if (!imageId || !cameraIdentCode) {
          return of(ImageActions.confirmVideoRequestFailed({ imageId: imageId ?? '', error: VideoRequestErrorValues.MISSING_PENDING_DATA }));
        }
        return of(ImageActions.confirmVideoRequest({ imageId, cameraIdentCode }));
      })
    )
  );

  openShop$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ImageActions.openShop),
        switchMap(() => this.shopService.openShop())
      ),
    { dispatch: false }
  );

  upgradeOrOpenServicePlan$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ImageActions.upgradeServicePlan, ImageActions.openServicePlan),
        switchMap(() => this.shopService.openSubscriptionManager())
      ),
    { dispatch: false }
  );

  requestVideoSucceeded$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ImageActions.requestVideoSucceeded),
        switchMap(() =>
          from(
            this.toastController.create({
              message: this.transloco.translate('gallery.imageViewer.actions.videoRequestedToast'),
              duration: 2000,
              position: 'bottom',
              color: 'success',
            })
          ).pipe(tap(toast => toast.present()))
        )
      ),
    { dispatch: false }
  );
}
